import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Date-only formatter.
 *
 * `locale` is optional and defaults to 'pt-BR', so every existing caller keeps
 * its behaviour; i18n-aware call sites pass the active language (Task 14 fix
 * round MINOR 17 — the billing pages rendered pt-BR dates in es/en UIs).
 */
export function formatDate(
  isoString: string,
  timeZone: string = "UTC",
  locale: string = "pt-BR",
): string {
  const date = new Date(isoString);
  try {
    return date.toLocaleDateString(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone,
    });
  } catch {
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  }
}

export function formatDateTime(isoString: string, timeZone: string = "UTC"): string {
  const date = new Date(isoString);
  try {
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone,
    });
  } catch {
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "UTC",
    });
  }
}

export function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

export function getStatusColor(status: string): string {
  switch (status) {
    case "online": return "bg-emerald-500";
    case "offline": return "bg-slate-400";
    case "degraded": return "bg-amber-500";
    case "suspended": return "bg-red-500";
    default: return "bg-slate-300";
  }
}

/**
 * Format a Date into local 'YYYY-MM' (never UTC / toISOString).
 */
export function formatLocalPeriod(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

/**
 * Format a Date or date string into local 'YYYY-MM-DD' (never UTC / toISOString).
 */
export function formatLocalDate(input: Date | string = new Date()): string {
  if (typeof input === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(input);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    const d = new Date(input);
    if (!Number.isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return input;
  }
  const year = input.getFullYear();
  const month = String(input.getMonth() + 1).padStart(2, "0");
  const day = String(input.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

